import React, { useState, useEffect, useCallback } from "react";
import {
  listBankAccounts,
  listBankAccountsPortal,
  createBankAccount,
  createBankAccountPortal,
  deleteBankAccount,
  deleteBankAccountPortal
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Table,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  Plus,
  Landmark
} from "lucide-react";
import {
  useToast
} from "../../context/ToastContext";

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Collection", value: "COLLECTION" },
  { label: "Reconciliation", value: "RECONCILIATION" },
  { label: "General", value: "GENERAL" },
];

const emptyForm = {
  accountName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "COLLECTION",
  isDefault: false,
};

const BankAccountsPage = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await listBankAccountsPortal() : await listBankAccounts();
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load bank accounts.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isPortal) await createBankAccountPortal(form);
      else await createBankAccount(form);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to create bank account.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this bank account?")) return;
    try {
      if (isPortal) await deleteBankAccountPortal(id);
      else await deleteBankAccount(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to delete bank account.");
    }
  };

  const columns = [
    { key: "accountName", header: "Account name" },
    { key: "bankName", header: "Bank" },
    {
      key: "accountNumber",
      header: "Account number",
      render: (row) => (
        <span className="font-mono text-sm text-neutral-700 dark:text-neutral-200">{row.accountNumber}</span>
      ),
    },
    {
      key: "accountType",
      header: "Type",
      render: (row) => (
        <Badge variant="primary">{(row.accountType || "").replace(/_/g, " ")}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-danger-500 hover:text-danger-700"
          onClick={() => handleDelete(row._id)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Bank accounts</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Manage collection and reconciliation accounts used for fee payments.
        </p>
      </div>

      

      <Card>
        <CardHeader>
          <CardTitle>Add bank account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Account name"
              placeholder="e.g. Primary collection account"
              required
              value={form.accountName}
              onChange={(e) => setForm({ ...form, accountName: e.target.value })}
            />
            <Input
              label="Bank name"
              placeholder="e.g. HDFC Bank"
              required
              value={form.bankName}
              onChange={(e) => setForm({ ...form, bankName: e.target.value })}
            />
            <Input
              label="Account number"
              placeholder="Enter account number"
              required
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
            />
            <Input
              label="IFSC code"
              placeholder="e.g. HDFC0001234"
              value={form.ifscCode}
              onChange={(e) => setForm({ ...form, ifscCode: e.target.value })}
            />
            <Select
              label="Account type"
              options={ACCOUNT_TYPE_OPTIONS}
              value={form.accountType}
              onChange={(value) => setForm({ ...form, accountType: value })}
            />
            <div className="flex items-end">
              <Button type="submit" isLoading={submitting} leftIcon={<Plus className="h-4 w-4" />}>
                Add account
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          <div className="flex gap-4">
            <Skeleton height={12} width="22%" />
            <Skeleton height={12} width="18%" />
            <Skeleton height={12} width="25%" />
            <Skeleton height={12} width="15%" />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="22%" />
              <Skeleton height={16} width="18%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          data={rows}
          emptyState={
            <EmptyState
              icon={Landmark}
              title="No bank accounts yet"
              description="Add a collection or reconciliation account to get started."
            />
          }
        />
      )}
    </div>
  );
};

export default BankAccountsPage;